/**
 * Auth Circuit Breaker
 * Prevents token refresh loops and excessive API calls
 */

interface CircuitBreakerState {
  tokenRefreshCount: number;
  lastResetTime: number;
  isBlocked: boolean;
  profileUpdateErrors: number;
}

class AuthCircuitBreaker {
  private state: CircuitBreakerState = {
    tokenRefreshCount: 0,
    lastResetTime: Date.now(),
    isBlocked: false,
    profileUpdateErrors: 0
  };

  private readonly MAX_TOKEN_REFRESHES = 3; // Max 3 refreshes per minute
  private readonly RESET_INTERVAL = 60000; // 1 minute
  private readonly BLOCK_DURATION = 60000; // Block for 1 minute
  private readonly MAX_PROFILE_ERRORS = 2; // Max 2 profile errors before blocking

  checkTokenRefresh(): boolean {
    this.resetIfNeeded();
    
    if (this.state.isBlocked) {
      console.log('🚫 [Circuit Breaker] Token refresh blocked - cooling down');
      return false;
    }

    this.state.tokenRefreshCount++;
    
    if (this.state.tokenRefreshCount > this.MAX_TOKEN_REFRESHES) {
      console.warn('🔥 [Circuit Breaker] Too many token refreshes, blocking for 1 minute');
      this.state.isBlocked = true;
      
      setTimeout(() => {
        this.state.isBlocked = false;
        this.state.tokenRefreshCount = 0;
        console.log('✅ [Circuit Breaker] Reset - token refreshes allowed again');
      }, this.BLOCK_DURATION);
      
      return false;
    }

    return true;
  }

  recordProfileError(): void {
    this.state.profileUpdateErrors++;
    
    if (this.state.profileUpdateErrors > this.MAX_PROFILE_ERRORS) {
      console.warn('🚫 [Circuit Breaker] Too many profile errors, blocking profile updates');
      this.state.isBlocked = true;
      
      setTimeout(() => {
        this.state.isBlocked = false;
        this.state.profileUpdateErrors = 0;
        console.log('✅ [Circuit Breaker] Profile updates re-enabled');
      }, this.BLOCK_DURATION);
    }
  }

  canUpdateProfile(): boolean {
    return !this.state.isBlocked && this.state.profileUpdateErrors <= this.MAX_PROFILE_ERRORS;
  }

  private resetIfNeeded(): void {
    const now = Date.now();
    if (now - this.state.lastResetTime > this.RESET_INTERVAL) {
      this.state.tokenRefreshCount = 0;
      this.state.lastResetTime = now;
    }
  }

  getStatus(): CircuitBreakerState {
    return { ...this.state };
  }

  reset(): void {
    this.state = {
      tokenRefreshCount: 0,
      lastResetTime: Date.now(),
      isBlocked: false,
      profileUpdateErrors: 0
    };
    console.log('🔄 [Circuit Breaker] Manually reset');
  }
}

export const authCircuitBreaker = new AuthCircuitBreaker();

// Make available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).authCircuitBreaker = authCircuitBreaker;
} 